import type { IntlShape } from 'react-intl';
import type { ParcelDocType, SampleDocType } from '@ast-data/shared';
import type { ConflictDoc } from './conflictHandler';
import { formatDate } from '../../utils/formatDate';
import { areaDescriptionLocation } from '../findings/areaDescriptionLabel';
import { parcelPickerLabel } from '../findings/parcelPickerLabel';
import { sampleLabel } from '../findings/sampleLabel';

/**
 * Entity types whose i18n field/entity labels live under a namespace that
 * differs from the entity `name`. Everything else uses its own name as the
 * prefix.
 */
const LABEL_PREFIX_OVERRIDE: Record<string, string> = {
    findingPlanum: 'planum',
    findingProfile: 'profile'
};

/** The i18n label namespace for an entity type (see LABEL_PREFIX_OVERRIDE). */
export function labelPrefixFor(entityType: string): string {
    return LABEL_PREFIX_OVERRIDE[entityType] ?? entityType;
}

/**
 * A human, singular label for the conflicting entity ("Befund", "Fund", …),
 * falling back to the raw entity type when no `conflict.entity.<type>` key
 * exists.
 */
export function resolveEntityLabel(
    intl: IntlShape,
    entityType: string
): string {
    const id = `conflict.entity.${entityType}`;
    return intl.messages[id] ? intl.formatMessage({ id }) : entityType;
}

/**
 * The record fields likely to carry a human-readable identifier, in priority
 * order. First non-empty one (local preferred, else remote) names the record.
 */
const IDENTIFIER_FIELDS = [
    'shortTitle',
    'name',
    'displayName',
    'username',
    'measureNumber',
    'fileName',
    'number',
    'findingNumber',
    'findSlipNumber',
    'drawingSheetNumber',
    'title',
    'caption',
    'de'
];

function text(doc: ConflictDoc, field: string): string {
    const value = doc[field];
    return typeof value === 'string' ? value.trim() : '';
}

/**
 * Records without a name field of their own, named the way the rest of the
 * app names them: a diary day by its date, a Probe by type and date, a
 * Flächenbeschreibung by its Lage.
 */
const RECORD_NAMES: Record<
    string,
    (doc: ConflictDoc, intl: IntlShape) => string
> = {
    diary: (doc) => formatDate(text(doc, 'recordedAt')),
    timeEntry: (doc) =>
        [text(doc, 'workerName'), formatDate(text(doc, 'date'))]
            .filter(Boolean)
            .join(', '),
    sample: (doc, intl) =>
        sampleLabel(intl, {
            sampleType: text(doc, 'sampleType'),
            recordedAt: text(doc, 'recordedAt')
        } as Pick<SampleDocType, 'sampleType' | 'recordedAt'>),
    areaDescription: (doc) =>
        areaDescriptionLocation({
            area: text(doc, 'area'),
            planum: text(doc, 'planum'),
            sondage: text(doc, 'sondage')
        }),
    overallDescription: (doc) =>
        [text(doc, 'overallDescriptionNumber'), text(doc, 'designation')]
            .filter(Boolean)
            .join(' — '),
    // Without a number the label falls back to the id: no name, then.
    parcel: (doc) =>
        text(doc, 'parcelNumber')
            ? parcelPickerLabel(doc as unknown as ParcelDocType)
            : ''
};

function describeDoc(
    intl: IntlShape,
    entityType: string,
    doc: ConflictDoc
): string {
    const named = RECORD_NAMES[entityType];
    if (named) {
        return named(doc, intl);
    }
    for (const key of IDENTIFIER_FIELDS) {
        const value = doc[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
        if (typeof value === 'number') {
            return String(value);
        }
    }
    return '';
}

/**
 * A best-effort human identifier for the conflicting record (e.g. its short
 * title, file name or date). Empty when the record has no obviously nameable
 * field — the dialog then shows just the entity type.
 */
export function describeConflictRecord(
    intl: IntlShape,
    entityType: string,
    local: ConflictDoc,
    remote: ConflictDoc
): string {
    return (
        describeDoc(intl, entityType, local) ||
        describeDoc(intl, entityType, remote)
    );
}
